/* Copyright 2013 - 2024 Waiterio LLC */
import domains from './domains.js'
import IS_BROWSER from './IS_BROWSER.js'
import IS_DEVELOPMENT from './IS_DEVELOPMENT.js'
import IS_NODE from './IS_NODE.js'
import IS_STAGING from './IS_STAGING.js'
import IS_TESTING from './IS_TESTING.js'
import ports, { getClientOrServer } from './ports.js'
import PRODUCTION from './PRODUCTION.js'
import STAGING from './STAGING.js'

export default function getUrl({
  product,
  service,
  client = false,
  server = false,
  subdomain,
  forcedEnvironment,
}) {
  if (!service && subdomain) {
    service = 'whitelabel'
  }

  subdomain ||= service

  let { staging: domainStaging, production: domainProduction } =
    domains[product]
  let urlStaging = `https://${subdomain}.${domainStaging}`
  let urlProduction = `https://${subdomain}.${domainProduction}`
  let url = urlProduction
  let PRODUCT = product.toUpperCase()
  let SERVICE = service.toUpperCase()
  let PRODUCT_SERVICE_URL = `${PRODUCT}_${SERVICE}_URL`

  if (
    IS_BROWSER &&
    window[PRODUCT_SERVICE_URL] === `{{{${PRODUCT_SERVICE_URL}}}}`
  ) {
    delete window[PRODUCT_SERVICE_URL]
  }

  if (
    IS_NODE &&
    process.env[PRODUCT_SERVICE_URL] === `{{{${PRODUCT_SERVICE_URL}}}}`
  ) {
    delete process.env[PRODUCT_SERVICE_URL]
  }

  if (IS_BROWSER && window[PRODUCT_SERVICE_URL]) {
    url = `${window[PRODUCT_SERVICE_URL]}`
  } else if (IS_NODE && process.env[PRODUCT_SERVICE_URL]) {
    url = process.env[PRODUCT_SERVICE_URL]
  } else if (forcedEnvironment === PRODUCTION) {
    url = urlProduction
  } else if (IS_STAGING || forcedEnvironment === STAGING) {
    url = urlStaging
  } else if (IS_DEVELOPMENT || IS_TESTING) {
    if (IS_NODE) {
      let clientOrServer

      if (client) {
        clientOrServer = 'client'
      } else if (server) {
        clientOrServer = 'server'
      } else {
        clientOrServer = getClientOrServer(service)
      }

      let PORT =
        ports[product][service]?.[clientOrServer] ?? ports[product][service]

      url = `http://localhost:${PORT}`
    } else if (typeof window !== 'undefined' && window.location) {
      let hostname = window.location.hostname
      let isIpAddress = /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)

      if (isIpAddress) {
        let clientOrServer

        if (client) {
          clientOrServer = 'client'
        } else if (server) {
          clientOrServer = 'server'
        } else {
          clientOrServer = getClientOrServer(service)
        }

        let PORT =
          ports[product][service]?.[clientOrServer] ?? ports[product][service]

        let protocol = window.location.protocol?.replace(':', '') || 'http'

        url = `${protocol}://${hostname}:${PORT}`
      } else {
        let parts = hostname.split('.')
        let suffix = parts.length >= 3 ? parts.slice(2).join('.') : 'localhost'
        // Inherit the page's scheme. A phone reaching localhoster over a
        // tailnet name on plain http has no cert installed, and an https API
        // URL there would fail; hardcoding https by suffix also risks mixed
        // content in reverse. Falls back to the suffix heuristic only when
        // location.protocol is absent.
        let protocol =
          window.location.protocol?.replace(':', '') ||
          (suffix === 'localhost' ? 'http' : 'https')
        url = `${protocol}://${subdomain}.${product}.${suffix}`
      }
    } else {
      url = `http://${subdomain}.${product}.localhost`
    }
  }

  // if (IS_STAGING || IS_DEVELOPMENT) {
  //   console.debug(`${product}.${service}.url = ` + url)
  // }

  return url
}
